/**
 * TicketRequestModal Component
 *
 * Non-modal draggable wizard for requesting tickets to HMC events.
 * 3-page paginated flow:
 *   1. Guest Profile - Contact information
 *   2. Backup Selection - Choose backup events
 *   3. Consultant Contact - Benefits advisor follow-up preference
 *
 * Features:
 * - Draggable popup (non-modal, user can interact with page behind)
 * - Pagination stepper with circles and descriptions
 * - Minimize to floating button while browsing events
 * - Email only sent after completing all 3 pages
 * - Success notification on completion
 * - Full-screen mode on mobile in guest mode
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { X, Ticket, Users, Mail, Phone, Building2, User, CheckCircle, AlertCircle, ChevronDown, Minimize2, Calendar, GripHorizontal, ChevronLeft, ArrowLeft } from 'lucide-react';
import { Portal } from '@shared/components/ui/Portal';
import { apiClient } from '@shared/services/apiClient';
import { useNotificationStore } from '@shared/store/notificationStore';
import type { HMCEvent } from '../hmcEventsStore';
import { useHMCEventsStore } from '../hmcEventsStore';
import { getCleanEventName } from '../utils/attendanceParser';
import { parseLocalDate } from '../utils/dateUtils';

interface TicketRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: HMCEvent;
  /** All available events for backup selection dropdowns */
  allEvents?: HMCEvent[];
  onSubmitSuccess?: () => void;
  /** If true, show as standby request (for sold out events) */
  isStandbyRequest?: boolean;
  /** Callback when user minimizes to floating button */
  onMinimize?: () => void;
  /** Maximum tickets a guest can request (for guest mode) */
  maxTickets?: number;
}

interface TicketRequestFormData {
  fullName: string;
  company: string;
  title: string;
  email: string;
  phone: string;
  ticketCount: number;
  guestNames: string[];
  specialRequest: boolean;
  specialRequestNote: string;
  sendToEmail: string; // Optional: send request to a specific email address
}

type WizardPage = 1 | 2 | 3;

const PAGE_DESCRIPTIONS = {
  1: 'Guest Profile',
  2: 'Backup Selection',
  3: 'Consultant Contact',
};

export const TicketRequestModal: React.FC<TicketRequestModalProps> = ({
  isOpen,
  onClose,
  event,
  allEvents = [],
  onSubmitSuccess,
  isStandbyRequest = false,
  onMinimize,
  maxTickets,
}) => {
  const [currentPage, setCurrentPage] = useState<WizardPage>(1);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);

  // Track the selected primary event (First Choice) - can be changed on Page 2
  const [selectedPrimaryEvent, setSelectedPrimaryEvent] = useState<HMCEvent>(event);

  // Detect mobile for full-screen mode in guest view
  const [isMobile, setIsMobile] = useState(false);
  const isGuestMode = maxTickets !== undefined;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Use full-screen mode on mobile in guest mode
  const isFullScreen = isMobile && isGuestMode;

  // Track if user is typing (to hide header on mobile for more space)
  const [isTyping, setIsTyping] = useState(false);

  // Handle focus/blur on inputs to detect typing state and scroll into view
  const handleInputFocus = useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (isFullScreen) {
      setIsTyping(true);
      // Scroll the focused element into view after a short delay to allow keyboard to appear
      setTimeout(() => {
        e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [isFullScreen]);

  const handleInputBlur = useCallback(() => {
    // Small delay to prevent flicker when moving between fields
    setTimeout(() => {
      setIsTyping(false);
    }, 100);
  }, []);

  // Handle Enter key to move to next field
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const form = e.currentTarget.form;
      if (form) {
        const inputs = Array.from(form.querySelectorAll('input, textarea, select')) as HTMLElement[];
        const currentIndex = inputs.indexOf(e.currentTarget);
        const nextInput = inputs[currentIndex + 1];
        if (nextInput) {
          nextInput.focus();
        }
      }
    }
  }, []);

  // Dragging state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

  // Store actions for backup selection
  const {
    backupSelection,
    startBackupSelection,
    setSecondChoice,
    setThirdChoice,
    minimizeBackupSelection,
    clearBackupSelection,
  } = useHMCEventsStore();

  // Get first choice options (all events except second and third)
  const firstChoiceOptions = useMemo(() => {
    const excludeIds = new Set([backupSelection.secondChoiceEventId, backupSelection.thirdChoiceEventId].filter((id): id is number => id !== null));
    return allEvents.filter((e) => !excludeIds.has(e.id));
  }, [allEvents, backupSelection.secondChoiceEventId, backupSelection.thirdChoiceEventId]);

  // Get second choice options (exclude primary and third)
  const secondChoiceOptions = useMemo(() => {
    const excludeIds = new Set([selectedPrimaryEvent.id, backupSelection.thirdChoiceEventId].filter((id): id is number => id !== null));
    return allEvents.filter((e) => !excludeIds.has(e.id));
  }, [allEvents, selectedPrimaryEvent.id, backupSelection.thirdChoiceEventId]);

  // Get third choice options (exclude primary and second)
  const thirdChoiceOptions = useMemo(() => {
    const excludeIds = new Set([selectedPrimaryEvent.id, backupSelection.secondChoiceEventId].filter((id): id is number => id !== null));
    return allEvents.filter((e) => !excludeIds.has(e.id));
  }, [allEvents, selectedPrimaryEvent.id, backupSelection.secondChoiceEventId]);

  // Sync selectedPrimaryEvent when event prop changes
  useEffect(() => {
    setSelectedPrimaryEvent(event);
  }, [event]);

  // Check if this modal is being restored from a minimized backup selection
  useEffect(() => {
    if (isOpen && backupSelection.primaryEventId === event.id && backupSelection.formData && !backupSelection.isMinimized) {
      // Restored from minimized state - go directly to backup selection (page 2)
      setCurrentPage(2);
      setFormData(backupSelection.formData);
    }
  }, [isOpen, backupSelection.primaryEventId, backupSelection.formData, backupSelection.isMinimized, event.id]);

  // Center position on mount
  useEffect(() => {
    if (isOpen) {
      // Center the modal
      const centerX = Math.max(0, (window.innerWidth - 448) / 2);
      const centerY = Math.max(20, (window.innerHeight - 600) / 2);
      setPosition({ x: centerX, y: centerY });
    }
  }, [isOpen]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<TicketRequestFormData>({
    fullName: '',
    company: '',
    title: '',
    email: '',
    phone: '',
    ticketCount: 2,
    guestNames: ['', '', ''],
    specialRequest: false,
    specialRequestNote: '',
    sendToEmail: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * Handle drag start
   */
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, input, select, textarea')) return;

    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      posX: position.x,
      posY: position.y,
    };
    e.preventDefault();
  }, [position]);

  /**
   * Handle drag move
   */
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;

      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - 400, dragStartRef.current.posX + deltaX)),
        y: Math.max(0, Math.min(window.innerHeight - 100, dragStartRef.current.posY + deltaY)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  /**
   * Validate the form (page 1)
   */
  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    if (!formData.company.trim()) {
      newErrors.company = 'Company is required';
    }
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Validate send_to_email if provided
    if (formData.sendToEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.sendToEmail)) {
      newErrors.sendToEmail = 'Invalid email format';
    }

    // Validate guest names if more than 1 ticket
    if (formData.ticketCount > 1) {
      for (let i = 0; i < formData.ticketCount - 1; i++) {
        if (!formData.guestNames[i]?.trim()) {
          newErrors[`guest${i}`] = `Guest ${i + 1} name is required`;
        }
      }
    }

    // Validate special request note if special request is checked
    if (formData.specialRequest && !formData.specialRequestNote.trim()) {
      newErrors.specialRequestNote = 'Please describe your special request';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  /**
   * Handle "Next Page" from page 1 (Guest Profile)
   */
  const handlePage1Next = useCallback((e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Start backup selection in store and proceed to page 2
    startBackupSelection(event.id, formData);
    setCurrentPage(2);
  }, [validateForm, startBackupSelection, event.id, formData]);

  /**
   * Handle ticket count change
   */
  const handleTicketCountChange = (count: number) => {
    setFormData(prev => ({
      ...prev,
      ticketCount: count,
      specialRequest: count > 4 ? prev.specialRequest : false,
    }));
  };

  /**
   * Handle guest name change
   */
  const handleGuestNameChange = (index: number, value: string) => {
    setFormData(prev => {
      const newGuestNames = [...prev.guestNames];
      newGuestNames[index] = value;
      return { ...prev, guestNames: newGuestNames };
    });
  };

  /**
   * Handle minimize to floating button (from page 2)
   * Passes current position so floating button appears in same location
   */
  const handleMinimize = () => {
    minimizeBackupSelection(position);
    onMinimize?.();
    onClose();
  };

  /**
   * Handle changing the first choice (primary event) on page 2
   */
  const handleFirstChoiceChange = useCallback((eventId: number) => {
    const newPrimaryEvent = allEvents.find((e) => e.id === eventId);
    if (newPrimaryEvent) {
      setSelectedPrimaryEvent(newPrimaryEvent);
      // Update the backup selection store with the new primary event
      startBackupSelection(newPrimaryEvent.id, formData);
    }
  }, [allEvents, formData, startBackupSelection]);

  /**
   * Handle going back to page 1 from page 2
   */
  const handleBackToPage1 = useCallback(() => {
    setCurrentPage(1);
  }, []);

  /**
   * Handle going back to page 2 from page 3
   */
  const handleBackToPage2 = useCallback(() => {
    setCurrentPage(2);
  }, []);

  /**
   * Handle "Next Page" from page 2 (Backup Selection)
   */
  const handlePage2Next = () => {
    setCurrentPage(3);
  };

  /**
   * Format event date for display with availability status
   */
  /**
   * Format event option for dropdown display
   * Shows: Event Name (Date)
   */
  const formatEventOption = useCallback((e: HMCEvent) => {
    const parsedDate = parseLocalDate(e.event_date);
    const date = parsedDate ? parsedDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }) : '';
    const cleanName = getCleanEventName(e.opponent) || 'Event';
    return `${cleanName}${date ? ` (${date})` : ''}`;
  }, []);

  /**
   * Reset form and close
   */
  const handleClose = useCallback(() => {
    setCurrentPage(1);
    setFormData({
      fullName: '',
      company: '',
      title: '',
      email: '',
      phone: '',
      ticketCount: 2,
      guestNames: ['', '', ''],
      specialRequest: false,
      specialRequestNote: '',
      sendToEmail: '',
    });
    setErrors({});
    setShowSuccessNotification(false);
    onClose();
  }, [onClose]);

  /**
   * Handle final submission (page 3 - Consultant Contact)
   * This is where the email actually gets sent
   */
  const handleFinalSubmit = useCallback(async (wantsFollowup: boolean) => {
    setIsSubmitting(true);
    setErrors({});

    try {
      // Get backup event names for the email
      const secondEvent = backupSelection.secondChoiceEventId
        ? allEvents.find((e) => e.id === backupSelection.secondChoiceEventId)
        : null;
      const thirdEvent = backupSelection.thirdChoiceEventId
        ? allEvents.find((e) => e.id === backupSelection.thirdChoiceEventId)
        : null;

      // Build complete request payload with ALL collected data
      // Use selectedPrimaryEvent which may have been changed on Page 2
      const payload = {
        event_id: selectedPrimaryEvent.id,
        event_name: selectedPrimaryEvent.opponent || selectedPrimaryEvent.event_name || 'Event',
        event_date: selectedPrimaryEvent.event_date || '',
        event_venue: selectedPrimaryEvent.venue || null,
        full_name: formData.fullName,
        company: formData.company,
        title: formData.title,
        email: formData.email,
        phone: formData.phone || null,
        ticket_count: formData.ticketCount,
        guest_names: formData.guestNames.filter(name => name.trim()),
        special_request: formData.specialRequest,
        special_request_note: formData.specialRequestNote || null,
        // Include backup event names (these ARE included in the email)
        second_choice_event: secondEvent ? formatEventOption(secondEvent) : null,
        third_choice_event: thirdEvent ? formatEventOption(thirdEvent) : null,
        // Include benefits followup preference
        wants_benefits_followup: wantsFollowup,
        is_standby_request: isStandbyRequest,
        send_to_email: formData.sendToEmail.trim() || null,
      };

      // Submit ticket request to backend
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const isGuestModeLocal = maxTickets !== undefined;

      if (isGuestModeLocal) {
        // Guest mode: plain fetch without credentials
        const response = await fetch(`${apiBaseUrl}/api/hmc/ticket-request`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || 'Failed to submit ticket request');
        }
      } else {
        // Authenticated mode: use apiClient
        await apiClient.post('/api/hmc/ticket-request', payload);
      }

      setIsSubmitting(false);
      // Clear backup selection state
      clearBackupSelection();

      // Add notification to the store so it shows on dashboard
      const notificationStore = useNotificationStore.getState();
      notificationStore.addNotification({
        type: 'hmc_new_submission',
        title: 'New Ticket Request',
        message: `${formData.fullName} from ${formData.company} requested ${formData.ticketCount} ticket${formData.ticketCount > 1 ? 's' : ''} for ${selectedPrimaryEvent.opponent || selectedPrimaryEvent.event_name || 'Event'}`,
        severity: 'info',
        actionUrl: '/hmc-events?tab=requests',
        actionLabel: 'View Request',
        metadata: {
          event_id: selectedPrimaryEvent.id,
          full_name: formData.fullName,
          company: formData.company,
        },
      });

      // Show success notification
      setShowSuccessNotification(true);
      onSubmitSuccess?.();

      // Auto-close after showing notification
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error: unknown) {
      setIsSubmitting(false);
      console.error('Ticket request failed:', error);

      // Extract error message
      let errorMessage = 'Failed to submit ticket request. Please try again.';
      if (error instanceof Error) {
        errorMessage = error.message || errorMessage;
      } else if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { detail?: string } } };
        errorMessage = axiosError.response?.data?.detail || errorMessage;
      }

      setErrors({ submit: errorMessage });
    }
  }, [
    selectedPrimaryEvent,
    formData,
    isStandbyRequest,
    maxTickets,
    backupSelection,
    allEvents,
    formatEventOption,
    clearBackupSelection,
    handleClose,
    onSubmitSuccess,
  ]);

  if (!isOpen) return null;

  // Success notification view
  if (showSuccessNotification) {
    return (
      <Portal>
        {/* Full-screen success on mobile guest mode */}
        {isFullScreen ? (
          <div className="fixed inset-0 z-[500] bg-dark-bg flex items-center justify-center">
            <div className="w-full max-w-md px-6 text-center">
              <div className="w-20 h-20 rounded-full bg-green-500/30 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={40} className="text-green-400" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-3">
                Request Submitted!
              </h3>
              <p className="text-base text-dark-text leading-relaxed">
                Your ticket request has been submitted successfully. Your USI Benefits Consultant will reach out within 12-36 hours.
              </p>
            </div>
          </div>
        ) : (
          <div
            ref={dragRef}
            style={{
              position: 'fixed',
              left: position.x,
              top: position.y,
              zIndex: 400,
            }}
            className="w-full max-w-md"
          >
            <div className="bg-dark-surface border border-green-500/30 rounded-xl shadow-2xl overflow-hidden">
              <div className="bg-green-500/20 px-4 py-6 text-center">
                <div className="w-16 h-16 rounded-full bg-green-500/30 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  Request Submitted!
                </h3>
                <p className="text-sm text-dark-text">
                  Your ticket request has been submitted successfully. Your USI Benefits Consultant will reach out within 12-36 hours.
                </p>
              </div>
            </div>
          </div>
        )}
      </Portal>
    );
  }

  // Shared content for both full-screen and desktop modes
  const renderWizardContent = () => (
    <>
      {/* Pagination Stepper - hidden on mobile full-screen for more vertical space */}
      {!isFullScreen && (
        <div data-testid="wizard-stepper" className="px-4 py-4 border-b border-dark-border/30 bg-dark-header/50">
          <div className="flex items-center justify-center">
            {[1, 2, 3].map((page, idx) => (
              <React.Fragment key={page}>
                {/* Circle with page number */}
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                      currentPage === page
                        ? 'bg-brand-500 text-white ring-2 ring-brand-500/50'
                        : currentPage > page
                        ? 'bg-green-500 text-white'
                        : 'bg-dark-input border border-dark-border/30 text-dark-muted'
                    }`}
                  >
                    {currentPage > page ? (
                      <CheckCircle size={18} />
                    ) : (
                      page
                    )}
                  </div>
                  <span className={`text-xs mt-1 text-center max-w-[80px] ${
                    currentPage === page ? 'text-brand-400' : 'text-dark-muted'
                  }`}>
                    {PAGE_DESCRIPTIONS[page as WizardPage]}
                  </span>
                </div>

                {/* Connecting line */}
                {idx < 2 && (
                  <div
                    className={`w-12 h-0.5 mx-1 mt-[-16px] ${
                      currentPage > page ? 'bg-green-500' : 'bg-dark-border'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className={`flex-1 overflow-y-auto p-4 ${isFullScreen ? 'pb-safe-area-bottom' : ''}`}>
        {/* Page 1: Guest Profile */}
        {currentPage === 1 && (
          <form data-testid="wizard-page-1" onSubmit={handlePage1Next} className="space-y-4">
            {/* Event Info */}
            <div className={`p-3 rounded-lg ${isStandbyRequest ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-brand-500/10 border border-brand-500/20'}`}>
              <p className={`text-sm ${isStandbyRequest ? 'text-amber-300' : 'text-brand-300'}`}>
                <strong className="text-white">{getCleanEventName(event.opponent) || 'Event'}</strong>
                {event.event_date && ` - ${parseLocalDate(event.event_date)?.toLocaleDateString() ?? 'TBD'}`}
              </p>
              {event.venue && <p className={`text-xs mt-1 ${isStandbyRequest ? 'text-amber-400' : 'text-brand-400'}`}>{event.venue}</p>}
              {isStandbyRequest && (
                <p className="text-xs text-amber-300/80 mt-2 flex items-center gap-1">
                  <AlertCircle size={12} />
                  This event is sold out. Join the standby list.
                </p>
              )}
            </div>

            {/* Contact Information */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-dark-text uppercase tracking-wide">
                Contact Information
              </h3>

              {/* Full Name */}
              <div>
                <label className="flex items-center gap-2 text-sm text-dark-muted mb-1">
                  <User size={14} />
                  Full Name <span className="text-red-400">*</span>
                </label>
                <input
                  data-testid="guest-name-input"
                  type="text"
                  name="name"
                  autoComplete="name"
                  value={formData.fullName}
                  onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onKeyDown={handleKeyDown}
                  className={`w-full px-3 py-2 bg-dark-input border ${errors.fullName ? 'border-red-500' : 'border-dark-border/30'} rounded-lg text-white placeholder-dark-muted focus:outline-none focus:border-brand-500`}
                  placeholder="John Smith"
                />
                {errors.fullName && <p className="text-xs text-red-400 mt-1">{errors.fullName}</p>}
              </div>

              {/* Company */}
              <div>
                <label className="flex items-center gap-2 text-sm text-dark-muted mb-1">
                  <Building2 size={14} />
                  Company <span className="text-red-400">*</span>
                </label>
                <input
                  data-testid="guest-company-input"
                  type="text"
                  name="organization"
                  autoComplete="organization"
                  value={formData.company}
                  onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onKeyDown={handleKeyDown}
                  className={`w-full px-3 py-2 bg-dark-input border ${errors.company ? 'border-red-500' : 'border-dark-border/30'} rounded-lg text-white placeholder-dark-muted focus:outline-none focus:border-brand-500`}
                  placeholder="ABC Corporation"
                />
                {errors.company && <p className="text-xs text-red-400 mt-1">{errors.company}</p>}
              </div>

              {/* Title */}
              <div>
                <label className="flex items-center gap-2 text-sm text-dark-muted mb-1">
                  Title <span className="text-red-400">*</span>
                </label>
                <input
                  data-testid="guest-title-input"
                  type="text"
                  name="organization-title"
                  autoComplete="organization-title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onKeyDown={handleKeyDown}
                  className={`w-full px-3 py-2 bg-dark-input border ${errors.title ? 'border-red-500' : 'border-dark-border/30'} rounded-lg text-white placeholder-dark-muted focus:outline-none focus:border-brand-500`}
                  placeholder="HR Director"
                />
                {errors.title && <p className="text-xs text-red-400 mt-1">{errors.title}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="flex items-center gap-2 text-sm text-dark-muted mb-1">
                  <Mail size={14} />
                  Email <span className="text-red-400">*</span>
                </label>
                <input
                  data-testid="guest-email-input"
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onKeyDown={handleKeyDown}
                  className={`w-full px-3 py-2 bg-dark-input border ${errors.email ? 'border-red-500' : 'border-dark-border/30'} rounded-lg text-white placeholder-dark-muted focus:outline-none focus:border-brand-500`}
                  placeholder="john@company.com"
                />
                {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
              </div>

              {/* Phone (optional) */}
              <div>
                <label className="flex items-center gap-2 text-sm text-dark-muted mb-1">
                  <Phone size={14} />
                  Phone <span className="text-dark-muted">(optional)</span>
                </label>
                <input
                  data-testid="guest-phone-input"
                  type="tel"
                  name="tel"
                  autoComplete="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onKeyDown={handleKeyDown}
                  className="w-full px-3 py-2 bg-dark-input border border-dark-border/30 rounded-xl text-white placeholder-dark-muted focus:outline-none focus:border-brand-500"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            {/* Ticket Selection */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-dark-text uppercase tracking-wide flex items-center gap-2">
                <Users size={14} />
                Number of Tickets
              </h3>

              <div data-testid="ticket-count-selector" className="flex gap-2">
                {[1, 2, 3, 4].map((count) => (
                  <button
                    key={count}
                    type="button"
                    data-testid={`ticket-count-${count}`}
                    onClick={() => handleTicketCountChange(count)}
                    className={`flex-1 py-2 rounded-lg font-medium transition-all duration-150 ${
                      formData.ticketCount === count
                        ? 'bg-brand-500 text-white'
                        : 'bg-dark-input border border-dark-border/30 text-dark-text hover:border-brand-500'
                    }`}
                  >
                    {count}
                  </button>
                ))}
              </div>

              {/* Guest Names (if more than 1 ticket) */}
              {formData.ticketCount > 1 && (
                <div className="space-y-2 p-3 bg-dark-border/20 rounded-lg">
                  <p className="text-xs text-dark-muted">Please provide names of your guests:</p>
                  {Array.from({ length: formData.ticketCount - 1 }).map((_, idx) => (
                    <div key={idx}>
                      <input
                        type="text"
                        value={formData.guestNames[idx] || ''}
                        onChange={(e) => handleGuestNameChange(idx, e.target.value)}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        onKeyDown={handleKeyDown}
                        className={`w-full px-3 py-2 bg-dark-input border ${errors[`guest${idx}`] ? 'border-red-500' : 'border-dark-border/30'} rounded-lg text-white placeholder-dark-muted focus:outline-none focus:border-brand-500 text-sm`}
                        placeholder={`Guest ${idx + 1} name`}
                      />
                      {errors[`guest${idx}`] && (
                        <p className="text-xs text-red-400 mt-1">{errors[`guest${idx}`]}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Special Request (more than 4 tickets) */}
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.specialRequest}
                    onChange={(e) => setFormData(prev => ({ ...prev, specialRequest: e.target.checked }))}
                    className="mt-1 w-4 h-4 text-brand-500 bg-dark-input border-dark-border/30 rounded focus:ring-brand-400"
                  />
                  <div className="flex-1">
                    <span className="text-sm text-yellow-300 font-medium">
                      Request more than 4 tickets
                    </span>
                    <p className="text-xs text-yellow-400/80 mt-1">
                      Your consultant will contact you to discuss availability.
                    </p>
                  </div>
                </label>
                {formData.specialRequest && (
                  <div className="mt-3">
                    <textarea
                      value={formData.specialRequestNote}
                      onChange={(e) => setFormData(prev => ({ ...prev, specialRequestNote: e.target.value }))}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      className={`w-full px-3 py-2 bg-dark-input border ${errors.specialRequestNote ? 'border-red-500' : 'border-dark-border/30'} rounded-lg text-white placeholder-dark-muted focus:outline-none focus:border-brand-500 text-sm resize-none`}
                      placeholder="Describe your request (number needed, occasion, etc.)"
                      rows={3}
                    />
                    {errors.specialRequestNote && (
                      <p className="text-xs text-red-400 mt-1">{errors.specialRequestNote}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400 flex items-center gap-2">
                  <AlertCircle size={16} />
                  {errors.submit}
                </p>
              </div>
            )}

            {/* Next Page Button */}
            <button
              data-testid="wizard-next-button"
              type="submit"
              className={`w-full py-3 font-semibold rounded-lg transition-all duration-150 flex items-center justify-center gap-2 min-h-[48px] ${
                isStandbyRequest
                  ? 'bg-amber-500 hover:bg-amber-600 text-white'
                  : 'bg-brand-500 hover:bg-brand-600 text-white'
              }`}
            >
              Next Page
            </button>

            {/* Page indicator */}
            <div className="flex items-center justify-center gap-2 pt-2">
              <span className="w-2 h-2 rounded-full bg-brand-500" />
              <span className="w-2 h-2 rounded-full bg-dark-surface/50" />
              <span className="w-2 h-2 rounded-full bg-dark-surface/50" />
              <span className="text-xs text-dark-muted ml-2">Page 1 of 3</span>
            </div>
          </form>
        )}

        {/* Page 2: Backup Selection */}
        {currentPage === 2 && (
          <div data-testid="wizard-page-2" className="space-y-4">
            {/* Header with back and minimize buttons */}
            <div className="flex items-start justify-between pb-4 border-b border-dark-border/30">
              <div className="flex items-start gap-3">
                <button
                  data-testid="wizard-back-to-page-1"
                  onClick={handleBackToPage1}
                  className="p-2 -ml-2 text-dark-muted hover:text-white hover:bg-dark-surface/50 rounded-lg transition-all duration-150 min-w-[44px] min-h-[44px] flex items-center justify-center"
                  title="Go back to Guest Profile"
                >
                  <ArrowLeft size={18} />
                </button>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Select Backup Events</h3>
                  <p className="text-sm text-dark-muted">
                    Choose up to 2 backup events in case your first choice is unavailable.
                  </p>
                </div>
              </div>
              {!isFullScreen && (
                <button
                  data-testid="minimize-button"
                  onClick={handleMinimize}
                  className="p-2 text-dark-muted hover:text-white hover:bg-dark-surface/50 rounded-lg transition-all duration-150 min-w-[44px] min-h-[44px] flex items-center justify-center"
                  title="Minimize to browse events"
                >
                  <Minimize2 size={18} />
                </button>
              )}
            </div>

            {/* Minimize hint - only show on desktop */}
            {!isFullScreen && (
              <div className="flex items-center gap-2 p-3 bg-blue-500/10 border border-brand-500/20 rounded-lg">
                <Minimize2 size={16} className="text-blue-400 flex-shrink-0" />
                <p className="text-xs text-blue-300">
                  Click minimize to browse events. A floating button will appear to restore this wizard.
                </p>
              </div>
            )}

            {/* First Choice (Primary Event) - Editable Dropdown */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-white">
                <span className="w-6 h-6 rounded-full bg-brand-500 text-white text-xs flex items-center justify-center font-bold">1</span>
                First Choice (Primary)
              </label>
              <div className="relative">
                <select
                  data-testid="first-choice-select"
                  value={selectedPrimaryEvent.id}
                  onChange={(e) => handleFirstChoiceChange(Number(e.target.value))}
                  className="w-full px-3 py-3 bg-dark-input border border-dark-border/30 rounded-xl text-white appearance-none cursor-pointer focus:outline-none focus:border-brand-500 pr-10 [&>option]:bg-dark-surface [&>option]:text-white"
                >
                  {firstChoiceOptions.map((e) => (
                    <option key={e.id} value={e.id} className="bg-dark-surface text-white">
                      {formatEventOption(e)}
                    </option>
                  ))}
                </select>
                <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-muted pointer-events-none" />
              </div>
              {selectedPrimaryEvent.venue && (
                <p className="text-xs text-dark-muted ml-8">{selectedPrimaryEvent.venue}</p>
              )}
            </div>

            {/* Second Choice Dropdown */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-dark-text">
                <span className="w-6 h-6 rounded-full bg-dark-surface/50 text-white text-xs flex items-center justify-center font-bold">2</span>
                Second Choice (Optional)
              </label>
              <div className="relative">
                <select
                  data-testid="second-choice-select"
                  value={backupSelection.secondChoiceEventId || ''}
                  onChange={(e) => setSecondChoice(e.target.value ? Number(e.target.value) : null)}
                  className="w-full px-3 py-3 bg-dark-input border border-dark-border/30 rounded-xl text-white appearance-none cursor-pointer focus:outline-none focus:border-brand-500 pr-10 [&>option]:bg-dark-surface [&>option]:text-white"
                >
                  <option value="" className="bg-dark-surface text-dark-muted">Select a backup event...</option>
                  {secondChoiceOptions.map((e) => (
                    <option key={e.id} value={e.id} className="bg-dark-surface text-white">
                      {formatEventOption(e)}
                    </option>
                  ))}
                </select>
                <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-muted pointer-events-none" />
              </div>
              {backupSelection.secondChoiceEventId && (
                <button
                  onClick={() => setSecondChoice(null)}
                  className="text-xs text-red-400 hover:text-red-300"
                >
                  Clear selection
                </button>
              )}
            </div>

            {/* Third Choice Dropdown */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-dark-text">
                <span className="w-6 h-6 rounded-full bg-dark-surface/50 text-white text-xs flex items-center justify-center font-bold">3</span>
                Third Choice (Optional)
              </label>
              <div className="relative">
                <select
                  data-testid="third-choice-select"
                  value={backupSelection.thirdChoiceEventId || ''}
                  onChange={(e) => setThirdChoice(e.target.value ? Number(e.target.value) : null)}
                  className="w-full px-3 py-3 bg-dark-input border border-dark-border/30 rounded-xl text-white appearance-none cursor-pointer focus:outline-none focus:border-brand-500 pr-10 [&>option]:bg-dark-surface [&>option]:text-white"
                >
                  <option value="" className="bg-dark-surface text-dark-muted">Select a backup event...</option>
                  {thirdChoiceOptions.map((e) => (
                    <option key={e.id} value={e.id} className="bg-dark-surface text-white">
                      {formatEventOption(e)}
                    </option>
                  ))}
                </select>
                <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-muted pointer-events-none" />
              </div>
              {backupSelection.thirdChoiceEventId && (
                <button
                  onClick={() => setThirdChoice(null)}
                  className="text-xs text-red-400 hover:text-red-300"
                >
                  Clear selection
                </button>
              )}
            </div>

            {/* Summary */}
            {(backupSelection.secondChoiceEventId || backupSelection.thirdChoiceEventId) && (
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <p className="text-sm text-green-300 flex items-center gap-2">
                  <CheckCircle size={16} />
                  {backupSelection.secondChoiceEventId && backupSelection.thirdChoiceEventId
                    ? '2 backup events selected'
                    : '1 backup event selected'}
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2 pt-2">
              <button
                data-testid="skip-backups-button"
                onClick={() => {
                  clearBackupSelection();
                  // Re-start backup selection to preserve form data without actual backups
                  startBackupSelection(selectedPrimaryEvent.id, formData);
                  setCurrentPage(3);
                }}
                className="flex-1 py-3 px-4 bg-dark-input border border-dark-border/30 hover:border-brand-500 text-dark-text font-medium rounded-lg transition-all duration-150 text-sm min-h-[48px]"
              >
                Skip Backups
              </button>
              <button
                data-testid="wizard-page-2-next-button"
                onClick={handlePage2Next}
                className="flex-1 py-3 px-4 bg-brand-500 hover:bg-brand-600 text-white font-medium rounded-lg transition-all duration-150 text-sm flex items-center justify-center gap-2 min-h-[48px]"
              >
                Next Page
              </button>
            </div>

            {/* Page indicator */}
            <div className="flex items-center justify-center gap-2 pt-2">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              <span className="w-2 h-2 rounded-full bg-brand-500" />
              <span className="w-2 h-2 rounded-full bg-dark-surface/50" />
              <span className="text-xs text-dark-muted ml-2">Page 2 of 3</span>
            </div>
          </div>
        )}

        {/* Page 3: Consultant Contact */}
        {currentPage === 3 && (
          <div data-testid="wizard-page-3" className="text-center py-6 space-y-5">
            {/* Back button */}
            <div className="flex justify-start">
              <button
                data-testid="wizard-back-to-page-2"
                onClick={handleBackToPage2}
                className="p-2 -ml-2 text-dark-muted hover:text-white hover:bg-dark-surface/50 rounded-lg transition-all duration-150 min-w-[44px] min-h-[44px] flex items-center justify-center gap-1"
                title="Go back to Backup Selection"
              >
                <ArrowLeft size={18} />
                <span className="text-sm">Back</span>
              </button>
            </div>

            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto bg-brand-500/20">
              <Users size={32} className="text-brand-400" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white mb-3">
                Would You Like to Hear From Your Consultant?
              </h3>
              <p className="text-sm text-dark-text max-w-sm mx-auto leading-relaxed">
                Would you like your benefits advisor to follow up with you about your group's benefits before or after the event?
              </p>
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-left">
                <p className="text-sm text-red-400 flex items-center gap-2">
                  <AlertCircle size={16} />
                  {errors.submit}
                </p>
              </div>
            )}

            <div className="flex flex-col gap-3 pt-2">
              <button
                data-testid="followup-yes-button"
                onClick={() => handleFinalSubmit(true)}
                disabled={isSubmitting}
                className="w-full py-3 px-4 bg-brand-500 hover:bg-brand-600 disabled:bg-brand-500/50 text-white font-semibold rounded-lg transition-all duration-150 text-sm min-h-[48px] flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Yes, Please'
                )}
              </button>
              <button
                data-testid="followup-no-button"
                onClick={() => handleFinalSubmit(false)}
                disabled={isSubmitting}
                className="w-full py-3 px-4 bg-dark-input border border-dark-border/30 hover:border-gray-500 disabled:opacity-50 text-dark-text font-medium rounded-lg transition-all duration-150 text-sm min-h-[48px]"
              >
                No, Not Right Now
              </button>
            </div>

            {/* Disclaimer */}
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg mt-4">
              <div className="flex items-start gap-2">
                <AlertCircle size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-300 text-left">
                  Tickets are first come, first served. All requests go through an approval process. Your consultant will reach out within 12-36 hours.
                </p>
              </div>
            </div>

            {/* Page indicator */}
            <div className="flex items-center justify-center gap-2 pt-2">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              <span className="w-2 h-2 rounded-full bg-green-500" />
              <span className="w-2 h-2 rounded-full bg-brand-500" />
              <span className="text-xs text-dark-muted ml-2">Page 3 of 3</span>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <Portal>
      {/* Full-screen mode for mobile guest view */}
      {isFullScreen ? (
        <div className="fixed inset-0 z-[500] bg-dark-bg flex flex-col">
          {/* Mobile Full-Screen Header - hidden when typing for more vertical space */}
          {!isTyping && (
            <div className={`flex items-center justify-between px-4 py-3 border-b border-dark-border/30 ${isStandbyRequest ? 'bg-amber-900/30' : 'bg-dark-surface'}`}>
              <button
                data-testid="ticket-request-back-button"
                onClick={handleClose}
                className="p-2 -ml-2 text-dark-muted hover:text-white rounded-lg transition-all duration-150 min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <ChevronLeft size={24} />
              </button>
              <div className="flex items-center gap-2">
                <Ticket size={20} className={isStandbyRequest ? 'text-amber-400' : 'text-brand-400'} />
                <h2 className="text-lg font-semibold text-white">
                  {PAGE_DESCRIPTIONS[currentPage]}
                </h2>
              </div>
              <div className="w-[44px]" /> {/* Spacer for centering */}
            </div>
          )}

          {/* Render shared wizard content */}
          {renderWizardContent()}
        </div>
      ) : (
        /* Non-modal draggable popup for desktop */
        <div
          ref={dragRef}
          style={{
            position: 'fixed',
            left: position.x,
            top: position.y,
            zIndex: 400,
            cursor: isDragging ? 'grabbing' : 'auto',
          }}
          className="w-full max-w-md"
        >
          <div data-testid="ticket-request-modal" className="bg-gradient-to-br from-dark-surface/95 to-dark-bg/85 backdrop-blur-sm border border-dark-border/30 rounded-xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col">
            {/* Draggable Header */}
            <div
              className={`flex items-center justify-between px-4 py-3 border-b border-dark-border/30 ${isStandbyRequest ? 'bg-amber-900/30' : 'bg-dark-header'} cursor-grab select-none`}
              onMouseDown={handleDragStart}
            >
              <div className="flex items-center gap-2">
                <GripHorizontal size={16} className="text-dark-muted" />
                <Ticket size={20} className={isStandbyRequest ? 'text-amber-400' : 'text-brand-400'} />
                <h2 className="text-lg font-semibold text-white">
                  {PAGE_DESCRIPTIONS[currentPage]}
                </h2>
              </div>
              <button
                data-testid="ticket-request-close-button"
                onClick={handleClose}
                className="p-2 text-dark-muted hover:text-white hover:bg-dark-surface/50 rounded-lg transition-all duration-150 min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X size={20} />
              </button>
            </div>

            {/* Render shared wizard content */}
            {renderWizardContent()}
          </div>
        </div>
      )}
    </Portal>
  );
};

export default TicketRequestModal;
